import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import widgetRegistry from './widgetRegistry';
import { WIDGET_CATALOG, CATALOG_CATEGORY_ORDER, buildPreviewConfig, stylesForType } from './widgetCatalog';
import WidgetMiniPreview, { useInView } from './WidgetMiniPreview';
import CustomWidgetBuilder from './CustomWidgetBuilder';

export const WIDGET_DND_TYPE = 'DASHBOARD_WIDGET';

const WidgetTile = ({ entry, selected, onSelect, onDrill }) => {
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: WIDGET_DND_TYPE,
        item: { widgetType: entry.type, variantId: entry.variant },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }), [entry.type, entry.variant]);
    const [previewRef, inView] = useInView();
    const previewConfig = useMemo(() => buildPreviewConfig(entry), [entry]);

    const activate = () => {
        if (entry.hasStyles) onDrill(entry.type);
        else onSelect(entry);
    };

    return (
        <div
            ref={dragRef}
            role="button"
            tabIndex={0}
            className={`widget-tile${selected ? ' widget-tile--selected' : ''}${isDragging ? ' widget-tile--dragging' : ''}`}
            title={entry.hasStyles ? `${entry.description} — click to choose a style` : entry.description}
            onClick={activate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    activate();
                }
            }}
        >
            <div className="widget-tile__preview" ref={previewRef}>
                {inView && <WidgetMiniPreview config={previewConfig} />}
            </div>
            <div className="widget-tile__label">{entry.label}</div>
            {entry.hasStyles && (
                <div className="widget-tile__styles-badge" title="Multiple styles available">
                    <i className="fas fa-layer-group"></i>
                </div>
            )}
        </div>
    );
};

const WidgetPalette = ({ onAddWidget, onCreateWidget }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [drillType, setDrillType] = useState(null);
    const [builderOpen, setBuilderOpen] = useState(false);
    const drawerRef = useRef(null);

    const grouped = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? WIDGET_CATALOG.filter((e) => e.label.toLowerCase().includes(q) || e.category.toLowerCase().includes(q))
            : WIDGET_CATALOG;
        const byCategory = {};
        filtered.forEach((entry) => {
            if (!byCategory[entry.category]) byCategory[entry.category] = [];
            byCategory[entry.category].push(entry);
        });
        return CATALOG_CATEGORY_ORDER
            .filter((cat) => byCategory[cat]?.length)
            .map((cat) => ({ category: cat, items: byCategory[cat] }));
    }, [search]);

    const drillMeta = drillType ? widgetRegistry[drillType] : null;
    const drillStyles = drillType ? stylesForType(drillType) : [];

    const close = () => {
        setOpen(false);
        setSelected(null);
        setSearch('');
        setDrillType(null);
    };

    const handleAdd = () => {
        if (!selected) return;
        onAddWidget(selected.type, selected.variant);
        close();
    };

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (drawerRef.current?.contains(e.target)) return;
            if (e.target.closest('.widget-fab')) return;
            close();
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    return (
        <>
            <button type="button" className="widget-fab" title="Add widget" onClick={() => setOpen(true)}>
                <i className="fas fa-plus"></i>
            </button>

            <aside className={`widget-drawer${open ? ' widget-drawer--open' : ''}`} ref={drawerRef}>
                {open && (
                    <>
                        <div className="widget-drawer__header">
                            <i className="fas fa-shapes"></i>
                            <span>Widget Library</span>
                            <button type="button" className="widget-drawer__close" title="Close" onClick={close}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {!drillType && (
                            <>
                                <button
                                    type="button"
                                    className="widget-drawer__custom-cta"
                                    onClick={() => setBuilderOpen(true)}
                                >
                                    <i className="fas fa-wand-magic-sparkles"></i> Create your own widget
                                </button>
                                <div className="widget-drawer__search">
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="text"
                                        placeholder="Search widgets, charts, gauges, 3D…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        <div className="widget-drawer__body">
                            {drillType ? (
                                <div className="widget-drawer__section">
                                    <button type="button" className="widget-drawer__back" onClick={() => setDrillType(null)}>
                                        <i className="fas fa-arrow-left"></i> Back to library
                                    </button>
                                    <div className="widget-drawer__section-title">
                                        {drillMeta?.label} — choose a style
                                        <span>{drillStyles.length}</span>
                                    </div>
                                    <div className="widget-drawer__grid">
                                        {drillStyles.map((style) => {
                                            const styleEntry = {
                                                key: `${drillType}::${style.id}`,
                                                type: drillType,
                                                variant: style.id,
                                                label: `${drillMeta.label} — ${style.label}`,
                                                description: drillMeta.description,
                                            };
                                            return (
                                                <WidgetTile
                                                    key={styleEntry.key}
                                                    entry={styleEntry}
                                                    selected={selected?.key === styleEntry.key}
                                                    onSelect={setSelected}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {grouped.map(({ category, items }) => (
                                        <div className="widget-drawer__section" key={category}>
                                            <div className="widget-drawer__section-title">
                                                {category}
                                                <span>{items.length}</span>
                                            </div>
                                            <div className="widget-drawer__grid">
                                                {items.map((entry) => (
                                                    <WidgetTile
                                                        key={entry.key}
                                                        entry={entry}
                                                        selected={selected?.key === entry.key}
                                                        onSelect={setSelected}
                                                        onDrill={setDrillType}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {!grouped.length && (
                                        <div className="widget-drawer__empty">No widgets match &quot;{search}&quot;.</div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="widget-drawer__footer">
                            <div className="widget-drawer__footer-info">
                                {selected ? selected.label : 'Select a widget above, or drag one onto the dashboard'}
                            </div>
                            <button type="button" className="widget-drawer__add-btn" disabled={!selected} onClick={handleAdd}>
                                <i className="fas fa-plus"></i> Add to Dashboard
                            </button>
                        </div>
                    </>
                )}
            </aside>

            <CustomWidgetBuilder
                open={builderOpen}
                onClose={() => setBuilderOpen(false)}
                onCreate={(config) => onCreateWidget(config)}
            />
        </>
    );
};

export default WidgetPalette;
